import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core'
import { select, Store } from '@ngrx/store'
import { MatDialog } from '@angular/material/dialog'
import { ComponentType } from '@angular/cdk/portal'
import { Size } from '@shared/size.mixin'
import { AppState } from '../../../core/store/index'
import { Observable, Subject } from 'rxjs'
import { map, take, takeUntil } from 'rxjs/operators'
import { getTodayDateTime } from './../../../credit/utils/credit.utils'
import { BackendService } from '../../../api/backend/backend.service'
import { CreditStructure } from '../../../credit/model/credit-structure.model'
import { CreditSubmissionStructure } from '../../../credit/model/credit-submission.model'
import { CREDIT_ADMIN_PERMISSION } from '../../../admin/model/credit-admin.model'
import { selectSecurity } from '../../../core/store/clients.selectors'
import { selectCurrentClientYears } from './../../../core/store/broker/broker.selectors'
import { CreditCreateSubmissionDialogContainerComponent } from '../../../credit/mi/design/credit-create-submission-dialog/credit-create-submission-dialog.container'
import { GseCreateSubmissionDialogContainerComponent } from '../../../credit/gse/design/gse-create-submission-dialog/gse-create-submission-dialog.container'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-credit-structure-card-container',
  styleUrls: ['./credit-structure-card.container.scss'],
  templateUrl: './credit-structure-card.container.html',
})
export class CreditStructureCardContainerComponent {
  canCreateSubmission$: Observable<boolean>
  private destroy$ = new Subject()
  imageURI: string
  structureYear: string
  structureProgram: string

  @Input() index: number
  @Input() structure: CreditStructure | CreditSubmissionStructure
  @Input() selectMultiple: boolean
  @Input() canCheck = false
  @Input() canOptimize = false
  @Input() isGse = false

  // Size mixin
  @Input() size: Size
  @Input() big: boolean
  @HostBinding('class.big')
  get isSizeBig() {
    return this.size === 'big'
  }

  @Output() structureClick = new EventEmitter<void>()
  @Output() structureCheckChange = new EventEmitter<void>()
  @Output() optimizeClick = new EventEmitter<void>()

  constructor(
    private backendService: BackendService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.canCreateSubmission$ = this.store.pipe(
      select(selectSecurity),
      map((security) => this.canOptimize && !!security.find((s) => s === CREDIT_ADMIN_PERMISSION)),
    )
    this.store.pipe(select(selectCurrentClientYears), take(1)).subscribe((years) => {
      const year = years.find((year) => {
        const program = year.studies.find((study) => Number(study.id) === this.structure.program_id)
        if (program) {
          this.structureProgram = program.name
        }
        return program
      })
      if (year) {
        this.structureYear = year.year
      }
    })
    if (!this.structure.imageSrc) {
      return
    }
    const timestamp = getTodayDateTime() // used to bust cache
    this.backendService
      .getThumbnailImage(this.structure.imageSrc, timestamp)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: Blob) => {
        this.createImageFromBlob(data)
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  onCreateSubmissionClick(): void {
    const dialogComponent: ComponentType<unknown> = this.isGse
      ? GseCreateSubmissionDialogContainerComponent
      : CreditCreateSubmissionDialogContainerComponent
    this.dialog.open(dialogComponent, {
      data: this.structure,
      minWidth: '30vw',
      maxWidth: '30vw',
    })
  }

  private createImageFromBlob(image: Blob): void {
    const reader = new FileReader()
    reader.onload = () => {
      this.imageURI = reader.result as string
      this.cdRef.markForCheck()
    }
    if (image) {
      reader.readAsDataURL(image)
    }
  }
}
